import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { firestoreService } from '../services/firestore.service';
import { cacheService } from '../services/cache.service';
import { createDebouncedSync, initializeFromFirestore, SYNC_TIMING } from '../services/syncHelper';

const defaultUserSettings = {
    calMinHour: 6,
    calMaxHour: 23,
    defaultTimeInterval: 15,
    lang: 'en',
    direction: 'ltr'
};

const defaultCustomCalendarColors = {};

export const useCalendarStore = create(
    persist(
        (set, get) => ({
            // Calendar data (runtime only - not synced)
            calendars: [],
            calendarsByKeys: {},
            accounts: [],

            // User preferences (synced to Firestore)
            selectedCalendars: {},
            currentUserCalendar: null,
            userCalendarSettings: defaultUserSettings,
            customCalendarColors: defaultCustomCalendarColors,

            // Firestore sync state
            userId: null,
            isInitialized: false,

            /**
             * Initialize calendar settings from Firestore
             */
            initializeWithUser: async (userId) => {
                set({ userId });

                await initializeFromFirestore({
                    getCached: () => cacheService.getUser()?.calendarSettings,
                    getFromFirestore: () => firestoreService.getCalendarSettings(userId),
                    onData: (settings) => {
                        if (settings) {
                            set({
                                selectedCalendars: settings.selectedCalendars || get().selectedCalendars,
                                currentUserCalendar: settings.currentUserCalendar || get().currentUserCalendar,
                                userCalendarSettings: settings.userCalendarSettings || get().userCalendarSettings,
                                customCalendarColors: settings.customCalendarColors || get().customCalendarColors,
                            });
                        }
                        set({ isInitialized: true });
                    },
                    onError: () => set({ isInitialized: true }),
                });
            },

            /**
             * Sync current calendar settings to Firestore (debounced)
             */
            syncCalendarSettings: () => {
                const { userId, selectedCalendars, currentUserCalendar, userCalendarSettings, customCalendarColors } = get();
                if (!userId) return;

                const settings = { selectedCalendars, currentUserCalendar, userCalendarSettings, customCalendarColors };

                createDebouncedSync(
                    'calendarSettings',
                    () => firestoreService.updateCalendarSettings(userId, settings),
                    () => cacheService.updateUser({ calendarSettings: settings }),
                    SYNC_TIMING.NORMAL
                )();
            },

            /**
             * Clear user data on logout
             */
            clearUserData: () => {
                set({ userId: null, isInitialized: false });
            },

            // Actions
            setCalendars: (calendars) => {
                const calByKeys = {};
                let firstCalendar = null;
                let firstCalendarId = null;

                for (const account of calendars) {
                    for (const cal of account.calendars) {
                        const key = `${account.email}-${cal.id}`;
                        calByKeys[key] = { ...cal, primary_email: account.email };
                        if (!firstCalendar) {
                            firstCalendar = calByKeys[key];
                            firstCalendarId = key;
                        }
                    }
                }

                const currentCalendar = get().currentUserCalendar;
                set({
                    calendars,
                    calendarsByKeys: calByKeys,
                    currentUserCalendar: currentCalendar || firstCalendarId
                });
            },

            setAccounts: (accounts) => set({ accounts }),

            addAccount: (account) => set((state) => ({
                accounts: [...state.accounts.filter(a => a.email !== account.email), account]
            })),

            setSelectedCalendars: (selected) => {
                set({ selectedCalendars: selected });
                get().syncCalendarSettings();
            },

            toggleCalendarSelection: (calendarKey, isSelected) => {
                set((state) => ({
                    selectedCalendars: {
                        ...state.selectedCalendars,
                        [calendarKey]: isSelected
                    }
                }));
                get().syncCalendarSettings();
            },

            setCurrentUserCalendar: (calendar) => {
                set({ currentUserCalendar: calendar });
                get().syncCalendarSettings();
            },

            setUserCalendarSettings: (settings) => {
                set((state) => ({
                    userCalendarSettings: { ...state.userCalendarSettings, ...settings }
                }));
                get().syncCalendarSettings();
            },

            // Get calendar by key
            getCalendarByKey: (key) => get().calendarsByKeys[key] || null,

            // Custom calendar colors
            setCustomCalendarColor: (calendarKey, color) => {
                set((state) => ({
                    customCalendarColors: {
                        ...state.customCalendarColors,
                        [calendarKey]: color
                    }
                }));
                get().syncCalendarSettings();
            },

            getCustomCalendarColor: (calendarKey) => get().customCalendarColors[calendarKey] || null,

            resetCustomCalendarColor: (calendarKey) => {
                set((state) => {
                    const { [calendarKey]: _removed, ...rest } = state.customCalendarColors;
                    return { customCalendarColors: rest };
                });
                get().syncCalendarSettings();
            },

            // Reset to defaults
            resetCalendarSettings: () => {
                set({
                    userCalendarSettings: defaultUserSettings
                });
                get().syncCalendarSettings();
            },
        }),
        {
            name: 'grownups-calendar-storage',
            partialize: (state) => ({
                selectedCalendars: state.selectedCalendars,
                currentUserCalendar: state.currentUserCalendar,
                userCalendarSettings: state.userCalendarSettings,
                customCalendarColors: state.customCalendarColors,
            }),
        }
    )
);

export { defaultUserSettings };
